import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../../Utils/persistHelpers"
import { defaultFiltersByFolders } from "../../../../Utils/utils"
import { logout } from "../../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../../User/redux/user.actions"
import { REQUESTS_AUTO_REFUND_SLICE_NAME } from "./requestsAutoRefund.constants"
import {
  deleteRequestsAutoRefund,
  getRequestsAutoRefund,
  getRequestsAutoRefundExport,
  getRequestsAutoRefundFilterOptions,
  updateRequestsAutoRefund
} from "./requestsAutoRefund.thunks"

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "created_timestamp", label: "Request date", active: true },
  { field_id: "updated_timestamp", label: "Update status date", active: true },
  { field_id: "current_amount", label: "Current amount", active: true },
  { field_id: "requested_amount", label: "Requested amount", active: true },
  { field_id: "user_full_name", label: "User full name", active: true },
  { field_id: "user_email", label: "User email", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "business_url", label: "Business URL", active: true },

  { field_id: "admin_comment", label: "Comment", active: true },
  { field_id: "admin_screenshots", label: "Screenshots", active: true }
]

const initialState = {
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [{ field_id: "request_status", label: "Status" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    request_status: "all",
    created_timestamp: null,
    updated_timestamp: null,
    request_type: "all",
    enrollments: [],
    merchant_descriptor: "all",
    arn_codes: [],
    user_full_name: [],
    user_email: [],
    business_account: [],
    business_url: [],
    enable_auto_refunds: "all",
    auto_refund_amount_other: { min: null, max: null, type: null },
    auto_refund_amount_rdr: { min: null, max: null, type: null },
    exclude_fields: [],
    is_archived: null,
    admin_comment: [],
    admin_screenshots: "all"
  },

  fetchingFilterOptions: false,
  filterOptions: {
    request_status: [],
    request_type: [],
    admin_comment: []
  },

  visibleFiltersAdmin: {
    request_status: true,
    user_email: true,
    user_full_name: true,
    business_account: true
  },

  exportFetching: false,
  exportResult: null,

  saveResult: null
}

const slice = createSlice({
  name: REQUESTS_AUTO_REFUND_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments — `requestsAutoRefundSetProp(key, value)`.
    // RTK actions carry a single payload, so callers pass `requestsAutoRefundSetProp({ key, value })`,
    // matching the `adminUsersSetProp`/`alertsSetProp` slices.
    requestsAutoRefundSetProp(state, action) {
      const { key, value } = action.payload
      if (["filters", "visibleFilters", "visibleFiltersAdmin"].includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearRequestsAutoRefundFilters(state, action) {
      state.filters = { ...initialState.filters, ...action.payload }
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- filter options ---------------- */
      .addCase(getRequestsAutoRefundFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(getRequestsAutoRefundFilterOptions.fulfilled, (state, action) => {
        state.fetchingFilterOptions = false
        state.filterOptions = action.payload
      })
      .addCase(getRequestsAutoRefundFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      /* ---------------- list ---------------- */
      .addCase(getRequestsAutoRefund.pending, (state) => {
        state.fetching = true
      })
      .addCase(getRequestsAutoRefund.fulfilled, (state, action) => {
        const result = action.payload
        state.fetching = false
        if (result?.results) {
          state.result = result
        } else {
          state.result = {
            count: result?.length || 0,
            results: result
          }
        }
      })
      .addCase(getRequestsAutoRefund.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- update ---------------- */
      .addCase(updateRequestsAutoRefund.pending, (state) => {
        state.fetching = true
      })
      .addCase(updateRequestsAutoRefund.fulfilled, (state, action) => {
        const autoRefund = action.payload
        let isChangedArchived = false
        state.result.results = state.result.results?.map((el) => {
          if (el.id === autoRefund.id) {
            if (el.is_archived !== autoRefund.is_archived) isChangedArchived = true
            return { ...el, request_status: autoRefund.request_status }
          }
          return el
        })
        state.fetching = false
        state.saveResult = isChangedArchived
          ? autoRefund.is_archived
            ? "archived"
            : "unarchived"
          : "updated"
      })
      .addCase(updateRequestsAutoRefund.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- delete ---------------- */
      .addCase(deleteRequestsAutoRefund.pending, (state) => {
        state.fetching = true
      })
      .addCase(deleteRequestsAutoRefund.fulfilled, (state, action) => {
        const { id } = action.payload
        state.result.results = state.result.results?.filter((el) => el.id !== id)
        state.result.count = state.result.count - 1
        state.fetching = false
      })
      .addCase(deleteRequestsAutoRefund.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- export ---------------- */
      .addCase(getRequestsAutoRefundExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getRequestsAutoRefundExport.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = action.payload
      })
      .addCase(getRequestsAutoRefundExport.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, (state) => ({
        ...initialState,
        filters: state.filters,
        columnsAdmin: state.columnsAdmin,
        visibleFiltersAdmin: state.visibleFiltersAdmin
      }))

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["requests-auto-refund"]?.data
        if (!preference || preference.version !== REQUESTS_AUTO_REFUND_PREFERENCES_VERSION) return
        if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
          return

        state.filters = preference.filters
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFiltersAdmin = preference.visibleFiltersAdmin
      })
  }
})

export const requestsAutoRefundActions = slice.actions

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const REQUESTS_AUTO_REFUND_PREFERENCES_VERSION = 1

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state, folder) => {
  const defaultFilters = defaultFiltersByFolders.auto_refund[folder] || initialState.filters

  const filtersToCompare = {
    ...initialState.filters,
    request_status: defaultFilters.request_status,
    is_archived: defaultFilters.is_archived
  }

  return isEqual(filtersToCompare, state.requestsAutoRefund.filters)
}

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "requestsAutoRefund",
    storage,
    // Only the user's own table setup survives a reload — the requests list, filter options,
    // and export/save results are all re-fetched or transient.
    whitelist: ["filters", "columnsAdmin", "visibleFiltersAdmin"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
